/**
 * Jarvis HUD chrome — the cinematic shell around the voice assistant (ADR-0025).
 *
 * Pure presentation: holographic grid backdrop + travelling scanline, corner
 * brackets that draw in on boot, side telemetry panels (real data: STT/TTS
 * edges, phase, tools used) with a synthetic equalizer and a rotating radar
 * sweep. Everything is CSS-only (transform/opacity animations), absolutely
 * positioned and `pointer-events-none`, so the interactive core (orb, input)
 * stays untouched. The logic lives in `VoiceAssistant`; this is the set design.
 */
import * as React from "react";

const CYAN = "#22d3ee";

export const HUD_KEYFRAMES = `
@keyframes hudIn{0%{opacity:0;transform:translateY(14px) scale(.98);filter:blur(6px)}100%{opacity:1;transform:none;filter:blur(0)}}
@keyframes hudCorner{0%{opacity:0;transform:scale(1.4)}100%{opacity:1;transform:scale(1)}}
@keyframes hudScan{0%{top:-12%}100%{top:112%}}
@keyframes hudGrid{to{background-position:0 48px}}
@keyframes hudBlink{0%,100%{opacity:1}50%{opacity:.25}}
@keyframes hudRadar{to{transform:rotate(360deg)}}
@keyframes hudEq{from{transform:scaleY(.25)}to{transform:scaleY(1)}}
@keyframes hudCaret{0%,100%{opacity:1}50%{opacity:0}}
@keyframes hudChipPulse{0%,100%{box-shadow:0 0 10px #22d3ee33}50%{box-shadow:0 0 22px #22d3ee66}}
`;

/** Staggered boot-in wrapper — everything in the HUD enters through this. */
export function BootIn({
  delay = 0,
  className,
  children,
  style,
}: {
  delay?: number;
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className={className} style={{ animation: `hudIn .55s cubic-bezier(0.2,0.8,0.2,1) ${delay}s both`, ...style }}>
      {children}
    </div>
  );
}

/** Holographic backdrop: slow-panning grid + travelling scanline + vignette. */
export function HudBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(${CYAN}0d 1px, transparent 1px), linear-gradient(90deg, ${CYAN}0d 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
          animation: "hudGrid 9s linear infinite",
          maskImage: "radial-gradient(ellipse at 50% 45%, black 35%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 45%, black 35%, transparent 78%)",
        }}
      />
      {/* travelling scanline */}
      <div
        className="absolute inset-x-0 h-28"
        style={{
          background: `linear-gradient(${CYAN}00, ${CYAN}14, ${CYAN}00)`,
          animation: "hudScan 8s linear infinite",
        }}
      />
      {/* vignette */}
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 180px 60px #000000cc" }} />
    </div>
  );
}

/** Corner brackets + frame ticks — the HUD's physical frame. */
export function HudFrame() {
  const corner = "pointer-events-none absolute h-14 w-14 border-cyan-400/50";
  return (
    <div className="pointer-events-none absolute inset-0">
      <span className={`${corner} left-4 top-4 border-l-2 border-t-2`} style={{ animation: "hudCorner .5s ease-out .1s both", filter: `drop-shadow(0 0 6px ${CYAN}66)` }} />
      <span className={`${corner} right-4 top-4 border-r-2 border-t-2`} style={{ animation: "hudCorner .5s ease-out .18s both", filter: `drop-shadow(0 0 6px ${CYAN}66)` }} />
      <span className={`${corner} bottom-4 left-4 border-b-2 border-l-2`} style={{ animation: "hudCorner .5s ease-out .26s both", filter: `drop-shadow(0 0 6px ${CYAN}66)` }} />
      <span className={`${corner} bottom-4 right-4 border-b-2 border-r-2`} style={{ animation: "hudCorner .5s ease-out .34s both", filter: `drop-shadow(0 0 6px ${CYAN}66)` }} />
      {/* top + bottom centre ticks */}
      <span className="absolute left-1/2 top-4 h-px w-40 -translate-x-1/2 bg-cyan-400/30" style={{ animation: "hudCorner .5s ease-out .4s both" }} />
      <span className="absolute bottom-4 left-1/2 h-px w-40 -translate-x-1/2 bg-cyan-400/30" style={{ animation: "hudCorner .5s ease-out .4s both" }} />
    </div>
  );
}

/** A labelled telemetry row: LABEL ······ VALUE. */
export function TeleRow({ label, value, tint = CYAN }: { label: string; value: React.ReactNode; tint?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.18em]">
      <span className="text-cyan-200/50">{label}</span>
      <span className="font-semibold" style={{ color: tint, textShadow: `0 0 8px ${tint}55` }}>
        {value}
      </span>
    </div>
  );
}

/** Synthetic equalizer — pure scenography, bottoms-anchored bars. */
export function Equalizer({ bars = 16, active = true }: { bars?: number; active?: boolean }) {
  return (
    <div className="flex h-10 items-end gap-[3px]">
      {Array.from({ length: bars }).map((_, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full"
          style={{
            height: 8 + ((i * 37) % 26),
            background: CYAN,
            opacity: active ? 0.75 : 0.2,
            transformOrigin: "bottom",
            animation: active ? `hudEq ${0.7 + (i % 5) * 0.14}s ease-in-out ${(i % 7) * 0.09}s infinite alternate` : undefined,
          }}
        />
      ))}
    </div>
  );
}

/** Rotating radar sweep with concentric rings. */
export function Radar({ size = 84 }: { size?: number }) {
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <span className="absolute inset-0 rounded-full border border-cyan-400/25" />
      <span className="absolute inset-[22%] rounded-full border border-cyan-400/20" />
      <span className="absolute inset-[44%] rounded-full border border-cyan-400/15" />
      <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-cyan-400/10" />
      <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-cyan-400/10" />
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(from 0deg, ${CYAN}55, ${CYAN}11 28%, transparent 40%)`,
          animation: "hudRadar 3.2s linear infinite",
          maskImage: "radial-gradient(circle, black 98%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(circle, black 98%, transparent 100%)",
        }}
      />
      <span className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: CYAN, boxShadow: `0 0 8px ${CYAN}` }} />
    </div>
  );
}

/** A bordered side telemetry panel (desktop only — hidden under lg). */
export function HudPanel({
  title,
  side,
  delay,
  children,
}: {
  title: string;
  side: "left" | "right";
  delay: number;
  children: React.ReactNode;
}) {
  return (
    <BootIn
      delay={delay}
      className={`pointer-events-none absolute top-1/2 hidden w-44 -translate-y-1/2 lg:block ${side === "left" ? "left-8" : "right-8"}`}
    >
      <div
        className="rounded-lg border border-cyan-400/20 px-3 py-3"
        style={{ background: "rgba(6,12,24,0.55)", backdropFilter: "blur(3px)", boxShadow: `0 0 24px ${CYAN}11` }}
      >
        <div className="mb-2 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: CYAN, animation: "hudBlink 1.6s ease-in-out infinite" }} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-cyan-200/70">{title}</span>
        </div>
        <div className="flex flex-col gap-2">{children}</div>
      </div>
    </BootIn>
  );
}
